import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { Redirect, Route, type RouteProps } from 'react-router-dom';

import { Alert } from '@notablehealth/design-system';
import { getLoginRedirectUrl, useAuthContext } from 'common';

interface Props extends Omit<RouteProps, 'component'> {
  component: React.ElementType;
}

export function PrivateRoute(props: Props) {
  const { component: Component, ...rest } = props;
  const context = useAuthContext();

  useEffect(() => {
    if (context.status !== 'resolved') {
      return;
    }

    const { user } = context;
    if (user) {
      const organizations = user.organizations.map((org) => org.id).join(', ');
      Sentry.setUser({
        id: user.id,
        email: user.email,
        organizations,
      });
      Sentry.setExtra('user_role', user.role);
    } else {
      Sentry.setUser(null);
      Sentry.setExtra('user_role', null);
    }
  }, [context]);

  if (context.status === 'pending') {
    return (
      <div className="h-100 d-flex align-items-center justify-content-center">
        Loading current user...
      </div>
    );
  }

  if (context.status === 'rejected') {
    return <Alert error={context.error} title="Failed to validate user authentication" />;
  }

  if (!context.user) {
    return <Redirect to={getLoginRedirectUrl('/login')} />;
  } else {
    return <Route {...rest} render={(props) => <Component {...props} />} />;
  }
}
